import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  SecurityContext,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Dictionary } from '@ngrx/entity';
import { createSelector, Store } from '@ngrx/store';
// date-fns
import isToday from 'date-fns/is_today';
import { get as _get, has as _has, some as _some } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import {
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import { PendingRegistrationsActions } from '@amfam/billing/registration/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { BillingStatusEnums } from '@amfam/billing/shared/ui';
import { env } from '@amfam/environment';
import {
  AutomaticPayment,
  BillAccount,
  BillingPaymentPaths,
  ONLINE_BILLING,
  PaymentStatus,
  ScheduledPayment
} from '@amfam/shared/models';
import { CurrencyUSDPipe, DocumentPipeService } from '@amfam/shared/ui/pipes';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  openWindowBlob,
  WindowRef
} from '@amfam/shared/utility/shared-services';
import { DsTableUtilService, LoadingSpinnerService } from '@amfam/ui-kit';

// Store

@Component({
  selector: 'ds-bill-account-manage',
  templateUrl: './bill-account-manage.component.html',
  styleUrls: ['./bill-account-manage.component.scss'],
  standalone: false
})
export class BillAccountManageComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('rowDetailTpl') rowDetailTpl: TemplateRef<unknown>;
  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('checkCellTpl', { static: true }) checkCellTpl: TemplateRef<unknown>;
  @ViewChild('statusCellTpl', { static: true }) statusCellTpl: TemplateRef<unknown>;
  @ViewChild('acctCellTpl', { static: true }) acctCellTpl: TemplateRef<unknown>;
  @ViewChild('dueDateCellTpl', { static: true }) dueDateCellTpl: TemplateRef<unknown>;
  @ViewChild('statementCellTpl', { static: true }) statementCellTpl: TemplateRef<unknown>;
  @ViewChild('billAccountsTable') table;

  // Data for the table to be built.
  rows: BillingRow[] = [];
  columns: BillingColumn[] = [];
  pageLimit = 10;

  rowHeight: string;
  footerHeight: string;
  headerHeight: string;

  cssClasses = 'material noshadow bill-acct-management-datatable';
  documentApi: string;
  showContent: string;
  contactNumber: string;

  dataLoading = true;
  public isAdminApp = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);

  selectedBillAccounts: string[] = [];

  rendererListenFunc: () => void;
  numberOfcheckedBillAccounts = 0;

  private billAccounts: BillAccount[] = [];
  private billAccountsNotificationEntities: Dictionary<BillingNotificationsState>;
  private scheduledPayments: ScheduledPayment[] = [];
  private automaticPayments: AutomaticPayment[] = [];

  // Map to keep track of checkbox state as subscriptions roll in - it's possible that the user checks the checkbox
  // Before all subscriptions have completed (on Safari) and that state will be lost when that happens.
  private checkMap: Map<string, boolean> = new Map();

  private disableMap: Map<string, boolean> = new Map();

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    //private store: Store<fromRoot.RootState>,
    private store: Store,
    public roleService: ImpersonateRolesService,
    private currencyUSDPipe: CurrencyUSDPipe = new CurrencyUSDPipe(),
    private renderer: Renderer2,
    private tableUtil: DsTableUtilService,
    private spinner: LoadingSpinnerService,
    private win: WindowRef,
    private sanitizer: DomSanitizer,
    private documentPipeService: DocumentPipeService,
    private applicationService: ApplicationService,
    private feature: FeatureFlagService,
    private deviceService: DeviceDetectorService
  ) {}

  ngOnInit() {
    // Reset state
    this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
    this.store.dispatch(PendingRegistrationsActions.PendingRegistrationsRemoveAll());

    // Table setup
    this.rowHeight = 'auto'; // Row height adjusts to content
    this.headerHeight = '60'; // Detail row height adjusts to content
    this.footerHeight = ''; // Detail row height adjusts to content
    // AS: Building the Download link
    this.documentApi = env.documentApi;
    this.initializeTable();

    this.store
      .select(
        createSelector(
          AutoPaySelectors.getAutomaticPayments,
          BillAccountsSelectors.selectBillAccounts,
          BillAccountsSelectors.selectBillAccountNotificationsEntities,
          ScheduledPaymentSelectors.selectScheduledPayments,
          AutoPaySelectors.getAnyLoading,
          ScheduledPaymentSelectors.selectScheduledPaymentsLoading,
          BillAccountsSelectors.selectBillAccountsLoading,
          (
            automaticPayments,
            billAccounts,
            billAccountsNotificationEntities,
            scheduledPayments,
            automaticPaymentsLoading,
            scheduledPaymentsLoading,
            billAccountsLoading
          ) => ({
            automaticPayments: automaticPayments,
            billAccounts: [...billAccounts],
            billAccountsNotificationEntities: billAccountsNotificationEntities,
            scheduledPayments: [...scheduledPayments],
            loading:
              scheduledPaymentsLoading ||
              billAccountsLoading ||
              automaticPaymentsLoading ||
              _some(billAccounts, { loading: true }) ||
              _some(automaticPayments, { loading: true })
          })
        )
      )
      .pipe(
        filter(state => !state.loading),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        // Set the loading to true while we handle incoming subscriptions.
        // The last thing in the subscrption is to pin this to the loading state,
        // This accounts for any slight delay while the bulk of the code is executed.
        this.dataLoading = true;

        // Early out if we're loading
        if (state.loading) {
          return;
        }

        // Set the bill accounts
        this.billAccounts = state.billAccounts;

        this.billAccountsNotificationEntities = state.billAccountsNotificationEntities;

        // Set the scheduled payments
        this.scheduledPayments = state.scheduledPayments;

        // Set the autopay rules
        this.automaticPayments = state.automaticPayments;

        // Setup the table row data
        this.formatRowData();

        this.dataLoading = state.loading;
      });

    // Set the contact number.
    this.contactNumber = env.numberLink.split(':')[1];
  }

  ngAfterViewInit() {
    /**
     * Reset the selection if the device orientation is changed.
     */
    this.rendererListenFunc = this.renderer.listen(window, 'orientationchange', () => {
      this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
      this.store.dispatch(PendingRegistrationsActions.PendingRegistrationsRemoveAll());
      this.selectedBillAccounts = [];
    });
  }

  rowCheckChanged(row: BillAccount, isChecked: boolean) {
    this.checkMap.set(row.billAccountNumber, isChecked);

    if (isChecked) {
      this.store.dispatch(
        pendingPaymentActions.AddPendingPaymentAction({
          billAccountNumber: row.billAccountNumber,
          pastDue: row.pastDue
        })
      );
      // If not registered, add to pending registrations
      if (_get(row, 'billingMethod') !== ONLINE_BILLING) {
        this.store.dispatch(
          PendingRegistrationsActions.PendingRegistrationsAddOne({
            associated: row.associated,
            policyNumber: row.policyList[0].policyNumber,
            billAccountNumber: row.billAccountNumber
          })
        );
      }

      const billAccountsEligibleForAutoPay: Observable<BillAccount[]> = this.store.select(
        AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay
      );

      billAccountsEligibleForAutoPay.pipe(take(1)).subscribe(billaccounts => {
        if (
          billaccounts.find(
            eligibleBillAccount => eligibleBillAccount.billAccountNumber === row.billAccountNumber
          )
        ) {
          this.numberOfcheckedBillAccounts++;
        }
      });

      // Check if account is past due, and disable all other non past due rows of different sourceSystem (advance or not)
      if (this.feature.isEnabled('classic_past_due') && _get(row, 'pastDue', false)) {
        this.rows.forEach(billRow => {
          if (
            !_get(billRow, 'accountObj.pastDue') ||
            _get(billRow, 'accountObj.isAdvance') !== _get(row, 'isAdvance')
          ) {
            billRow.disable = true;
            this.disableMap.set(billRow.billAccountNumber, true);
          }
        });
      } else {
        // If we aren't past due on this account, we must lock all accounts that are Past Due
        this.rows.forEach(billRow => {
          if (
            this.feature.isEnabled('classic_past_due') &&
            _get(billRow, 'accountObj.pastDue', false)
          ) {
            billRow.disable = true;
            this.disableMap.set(billRow.billAccountNumber, true);
          }
        });
      }
    } else {
      this.store.dispatch(
        pendingPaymentActions.RemovePendingPaymentAction({
          billAccountNumber: row.billAccountNumber
        })
      );
      if (_get(row, 'billingMethod') !== ONLINE_BILLING) {
        this.store.dispatch(
          PendingRegistrationsActions.PendingRegistrationsRemoveOne({
            associated: row.associated,
            policyNumber: row.policyList[0].policyNumber,
            billAccountNumber: row.billAccountNumber
          })
        );
      }

      const billAccountsEligibleForAutoPay: Observable<BillAccount[]> = this.store.select(
        AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay
      );

      billAccountsEligibleForAutoPay.pipe(take(1)).subscribe(billaccounts => {
        if (
          billaccounts.find(
            eligibleBillAccount => eligibleBillAccount.billAccountNumber === row.billAccountNumber
          )
        ) {
          this.numberOfcheckedBillAccounts--;
        }
      });

      // Check if Classic Past Due is enabled, and set the disable of all other rows to default
      if (this.feature.isEnabled('classic_past_due') && this.numberOfcheckedBillAccounts === 0) {
        this.rows.forEach(billRow => {
          billRow.disable = this.getDisableStatus(billRow.accountObj);
        });
      }
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    if (this.rendererListenFunc) {
      this.rendererListenFunc();
    }
  }

  getUnavailableMessage(row: unknown) {
    this.table.rowDetail.collapseAllRows();
    this.table.rowDetail.toggleExpandRow(row);
    this.showContent = 'managedElsewhere';
  }

  getAftMsg(row: unknown) {
    this.table.rowDetail.collapseAllRows();
    this.table.rowDetail.toggleExpandRow(row);
    this.showContent = 'aftContent';
  }

  onCloseRow() {
    this.table.rowDetail.collapseAllRows();
    this.showContent = '';
  }

  paySingleAccount(billAccount: BillAccount) {
    // Emit action to add pending payment.
    this.store.dispatch(
      pendingPaymentActions.AddPendingPaymentAction({
        billAccountNumber: billAccount.billAccountNumber
      })
    );

    // If not registered, add to pending registrations
    if (_get(billAccount, 'billingMethod') !== ONLINE_BILLING) {
      this.store.dispatch(
        PendingRegistrationsActions.PendingRegistrationsAddOne({
          associated: billAccount.associated,
          policyNumber: billAccount.policyList[0].policyNumber,
          billAccountNumber: billAccount.billAccountNumber
        })
      );

      // Route to registration
      this.store.dispatch(
        fromRouterActions.Go({
          path: [BillingPaymentPaths.BILLING_REGISTRATION]
        })
      );
      return;
    }

    // Route to add payment
    // ONE TIME PAYMENT ENTRYPOINT
    if (this.feature.isEnabled('simplified_payment')) {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
        })
      );
    } else {
      this.store
        .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
        .pipe(take(1))
        .subscribe(billAccounts => {
          if (billAccounts.length === 1) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
              })
            );
          }
        });
    }
  }

  // Clear the list of pending payments before we leave
  routeToDetailView(billAccountNumber: string) {
    this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
    this.store.dispatch(PendingRegistrationsActions.PendingRegistrationsRemoveAll());
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_DETAIL_PAGE, billAccountNumber]
      })
    );
  }

  onPageChanged() {
    /**
     * AS: Scroll to top. When the table reloads.
     * Bug: 230785
     */
    window.scrollTo(0, 0);
  }

  public openStatementOnAndroid(link: string) {
    let windowRef;
    if (!window.navigator) {
      windowRef = this.win.nativeWindow.open();
    }
    this.documentPipeService
      .retrieveDocument(link)
      .pipe(take(1))
      .subscribe(
        res => {
          if (!res) {
            if (windowRef) {
              windowRef.close();
            }
          }
          const url = this.sanitizer.sanitize(SecurityContext.URL, res);
          this.spinner.stop();
          openWindowBlob(this.win, url, res, windowRef, 'document.pdf');
        },
        () => {
          if (windowRef) {
            windowRef.close();
          }
        }
      );
  }

  public openStatement(link: string) {
    this.spinner.start();
    if (this.deviceService.device.toLowerCase() === 'android') this.openStatementOnAndroid(link);
    else {
      const windowRef = this.win.nativeWindow.open();
      this.documentPipeService
        .retrieveDocument(link)
        .pipe(take(1))
        .subscribe(
          res => {
            if (!res) {
              if (windowRef) {
                windowRef.close();
              }
            }
            const url = this.sanitizer.sanitize(SecurityContext.URL, res);
            this.spinner.stop();
            windowRef.location.href = url;
          },
          () => {
            if (windowRef) {
              windowRef.close();
            }
          }
        );
    }
  }

  // AS: Build the table for listing all accounts.
  // JC: Using pipes here so that we don't need custom templates for every cell
  private formatRowData() {
    this.rows = [];

    // Do everything on a temporary array until we're done to limit the repainting of the table.
    const rowsTemp: BillingRow[] = [];

    // Iterate over this.billAccounts to extract the desired row details and list here.
    if (this.billAccounts) {
      // Iterate over this.billAccounts to find the data with registered flag set and extract the row details and list here.
      for (const billAcc of this.billAccounts) {
        if (billAcc) {
          // TODO - determine if each bill accoiunt is on AutoPay, has a scheduled payment, or ... ??
          // and then determien the date the payment will go through, the date the

          // Determine current bill
          let billingDoc = null;
          if (
            _get(
              this.billAccountsNotificationEntities[billAcc.billAccountNumber],
              'documentsLoaded'
            ) === true &&
            _has(billAcc, 'billingDocumentsList.length')
          ) {
            const docs = billAcc.billingDocumentsList.slice();
            docs.sort(
              (a, b) => this.tableUtil.dateComparator(a.dateOnDocument, b.dateOnDocument) * -1
            );
            billingDoc = docs[0];
          }

          const isChecked = this.checkMap.get(billAcc.billAccountNumber);
          const isDisabled = this.disableMap.get(billAcc.billAccountNumber);
          const accountNickName = _get(billAcc, 'billingPreferences.accountNickName');
          const managedElsewhere = billAcc.registeredElsewhere || billAcc.isExternalBam;

          rowsTemp.push({
            checked: isChecked,
            billAccountNumber: billAcc.billAccountNumber,
            name: {
              name: accountNickName ? accountNickName : billAcc.billAccountNumber,
              isNickname: accountNickName ? true : false
            },
            status: managedElsewhere ? null : this.getAccountStatus(billAcc),
            unpaidBalance: managedElsewhere
              ? null
              : this.currencyUSDPipe.transform(billAcc.accountBalance),
            minimumDue: managedElsewhere
              ? null
              : {
                  dueAmount: this.currencyUSDPipe.transform(billAcc.minimumAmountDue),
                  dueDate: billAcc.paymentDueDate
                },
            accountObj: billAcc,
            currentBill: billingDoc,
            disable: isDisabled ? isDisabled : this.getDisableStatus(billAcc)
          });
        }
      }

      /**
       * AS: As per the final discussion of sorting, we are going to sort by
       * 1. Past Due
       * 2. Due To day.
       * 3. Due Soon and Date (When minimumdue is >0.
       * 4. Paid Thru Date. (When account balance is 0.)
       * 5. Nothing Due. (When minimum due is not available).
       * not have any additional sorting on tables.
       */
      const pastDueArr: BillingRow[] = [];
      const dueTodayArr: BillingRow[] = [];
      const dueSoonArr: BillingRow[] = [];
      const paidThruArr: BillingRow[] = [];
      const nothingDueArr: BillingRow[] = [];
      const defaultArr: BillingRow[] = [];
      rowsTemp.forEach(ba => {
        if (ba.status) {
          switch (ba.status.statusText) {
            case 'Past Due':
              pastDueArr.push(ba);
              break;
            case 'Due Today':
              dueTodayArr.push(ba);
              break;
            case 'Due Soon':
              dueSoonArr.push(ba);
              break;
            case 'Paid Thru':
              paidThruArr.push(ba);
              break;
            case 'Nothing Due':
              nothingDueArr.push(ba);
              break;
            default:
              defaultArr.push(ba);
              break;
          }
        } else {
          defaultArr.push(ba);
        }
      });
      this.rows = [
        ...pastDueArr,
        ...dueTodayArr,
        ...dueSoonArr,
        ...paidThruArr,
        ...nothingDueArr,
        ...defaultArr
      ];
    }

    // Determine if we need to show the pagination footer row
    if (this.billAccounts && this.billAccounts.length > this.pageLimit) {
      this.footerHeight = 'auto';
    } else {
      this.footerHeight = '';
    }
  }

  private initializeTable() {
    this.columns = [
      {
        headerTemplate: this.headerCellTpl,
        cellTemplate: this.checkCellTpl,
        name: ' ',
        prop: 'billAccountNumber',
        hideOnMobile: true,
        draggable: false,
        resizeable: false,
        sortable: false,
        canAutoResize: false,
        width: 100
      },
      {
        headerTemplate: this.headerCellTpl,
        cellTemplate: this.acctCellTpl,
        isHeaderOnMobile: true,
        name: 'Billing Account',
        shortName: 'Account',
        prop: 'name',
        draggable: false,
        resizeable: false,
        sortable: false,
        width: 180
      },
      {
        headerTemplate: this.headerCellTpl,
        cellTemplate: this.statusCellTpl,
        name: 'Account Status',
        shortName: 'Status',
        prop: 'status',
        draggable: false,
        resizeable: false,
        sortable: false,
        width: 110
      },
      {
        headerTemplate: this.headerCellTpl,
        name: 'Account Balance',
        shortName: 'Balance',
        prop: 'unpaidBalance',
        draggable: false,
        resizeable: false,
        sortable: false,
        width: 110
      },
      {
        headerTemplate: this.headerCellTpl,
        cellTemplate: this.dueDateCellTpl,
        name: 'Minimum Due',
        shortName: 'Min. Due',
        prop: 'minimumDue',
        draggable: false,
        resizeable: false,
        sortable: false,
        width: 110
      },
      {
        headerTemplate: this.headerCellTpl,
        cellTemplate: this.statementCellTpl,
        hideOnMobile: true,
        name: 'Current Bill',
        shortName: 'Bill',
        prop: 'currentBill',
        draggable: false,
        resizeable: false,
        sortable: false,
        width: 110
      }
    ];
  }

  /*
    Show account status in the following order:
    1. AFT
    2. Scheduled Payment
    3. Automatic Payment
    4. Payment due date (past due, due today, due soon, nothing due)
  */
  private getAccountStatus(accountObj: BillAccount) {
    const statusObj = {
      statusClass: '',
      statusText: '',
      statusDate: '',
      statusSubText: ''
    };

    // 1. If the account is AFT enabled, show AFT with the payment due date
    if (accountObj.enabledAFT) {
      statusObj.statusText = 'Scheduled';
      statusObj.statusDate = accountObj.paymentDueDate;
      return statusObj;
    }

    /*
        2. Determine if there are scheduled payments.In case of multiple schedule
        payment need to display the schedule payment date which is earliest.
      */
    const billAccountPayments: BillAccountPayments[] = [];
    this.scheduledPayments.forEach(payment => {
      if (payment) {
        payment.billAccounts.forEach(billAccountPayment => {
          billAccountPayments.push({
            billAccountNumber: billAccountPayment.billAccountNumber,
            billAcctPaymentAmount: billAccountPayment.billAcctPaymentAmount,
            receivedDate: payment.receivedDate,
            paymentStatus: payment.paymentStatus
          });
        });
      }
    });

    const matchingPayment = billAccountPayments
      .sort((a, b) => String(a['receivedDate']).localeCompare(String(b['receivedDate'])))
      .find(
        billAccountPayment => accountObj.billAccountNumber === billAccountPayment.billAccountNumber
      );

    if (matchingPayment) {
      statusObj.statusText =
        matchingPayment.paymentStatus === PaymentStatus.AUTHORIZED
          ? BillingStatusEnums.PROCESSING
          : BillingStatusEnums.SCHEDULED;
      statusObj.statusClass = 'success-600-text';
      statusObj.statusDate = matchingPayment.receivedDate;
      return statusObj;
    }

    // 3. If we dont' have a scheduled payment, determine if we have autopay information to show
    const matchingAutoPay = this.automaticPayments.find(
      (autopay: AutomaticPayment) =>
        _has(accountObj, 'billAccountNumber') &&
        _get(autopay, 'billAccountNumber', '').includes(accountObj.billAccountNumber)
      // && !autopay.hasError This would never have been set
    );

    if (matchingAutoPay) {
      statusObj.statusText = 'Auto Pay';
      statusObj.statusSubText =
        matchingAutoPay.paymentAmount === 'MinDue' ? 'Minimum due' : 'Account balance';
      statusObj.statusSubText +=
        matchingAutoPay.daysBeforeDueDate === '0'
          ? '\n paid on due date'
          : matchingAutoPay.daysBeforeDueDate === '1'
            ? '\n paid ' + matchingAutoPay.daysBeforeDueDate + ' day before due'
            : '\n paid ' + matchingAutoPay.daysBeforeDueDate + ' days before due';
      return statusObj;
    }

    // 4. If the bill account isnt on AFT, doesn't have a scheduled or automatic payment, determine what to show related to the due date
    if (Number(accountObj.minimumAmountDue) > 0) {
      // If the account is past due, show red text and past due text
      if (accountObj.pastDue === true) {
        statusObj.statusClass = 'secondary-600-text';
        statusObj.statusText = 'Past Due';
      } else {
        // If the due date is today, show Due Today
        if (isToday(accountObj.paymentDueDate)) {
          statusObj.statusText = 'Due Today';
        } else {
          statusObj.statusText = 'Due Soon';
          statusObj.statusDate = accountObj.paymentDueDate;
        }
      }
    } else {
      // No minimum Due, show nothing due
      statusObj.statusText = 'Nothing Due';
    }

    return statusObj;
  }

  // Bill account eligibility to make a payment.
  // If some bill accounts are already checked, we need to maintain the current disable status
  private getDisableStatus(billAcc: BillAccount): boolean {
    const billAcctHasAutoPay = this.automaticPayments.some(
      element => element?.billAccountNumber === billAcc.billAccountNumber
    );

    const disabled =
      this.numberOfcheckedBillAccounts > 0
        ? this.rows.find(row => row.billAccountNumber === billAcc.billAccountNumber)?.disable
        : billAcc.readOnly || (Number(billAcc.accountBalance) <= 0 && billAcctHasAutoPay);

    this.disableMap.set(billAcc.billAccountNumber, disabled);

    return disabled;
  }
}

interface BillingRow {
  checked: boolean;
  billAccountNumber: string;
  name: {
    name: string;
    isNickname: boolean;
  };
  status: {
    statusClass: string;
    statusText: string;
    statusDate: string;
    statusSubText: string;
  };
  unpaidBalance: string;
  minimumDue: {
    dueAmount: string;
    dueDate: string;
  };
  accountObj: BillAccount;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentBill: any;
  disable: boolean;
}

interface BillingColumn {
  headerTemplate: TemplateRef<unknown>;
  cellTemplate?: TemplateRef<unknown>;
  name: string;
  prop: string;
  hideOnMobile?: boolean;
  draggable: boolean;
  resizeable: boolean;
  sortable: boolean;
  canAutoResize?: boolean;
  width: number;
  isHeaderOnMobile?: boolean;
  shortName?: string;
}

interface BillAccountPayments {
  billAccountNumber: string;
  billAcctPaymentAmount: string;
  receivedDate: string;
  paymentStatus: string;
}
